import React, { Component } from "react";
import { Button, CircularProgress, createStyles, Theme, WithStyles, withStyles } from "@material-ui/core";
import { CloudOff, CloudDone, Sync as SyncIcon } from "@material-ui/icons";
import { resend, getSavedCallsCount, onChangeListener } from "../../api/failedCallsHandler";
import { withDetector } from "utils/withDetector";

interface State {
  count: number;
  syncing: boolean;
  lastSyncSuccess: boolean | null;
}

interface Props extends WithStyles {
  online: boolean;
}

class OfflineRequests extends Component<Props, State> {
  private wasOffline = false;
  private retryTimeout: NodeJS.Timeout | null = null;
  private successDismissTimeout: NodeJS.Timeout | null = null;

  state = {
    count: getSavedCallsCount(),
    syncing: false,
    lastSyncSuccess: null,
  };

  componentDidMount(): void {
    onChangeListener((offlineActivities: number, syncing?: boolean) => {
      this.setState({
        count: offlineActivities,
        syncing: syncing ?? this.state.syncing,
      });
    });

    // Auto-resend on mount if there are pending calls and we're online
    if (this.props.online && getSavedCallsCount() > 0) {
      this.triggerResend();
    }
  }

  componentDidUpdate(prevProps: Props): void {
    // Detect reconnection: was offline, now online
    if (!prevProps.online && this.props.online) {
      this.wasOffline = true;
      // Auto-resend when coming back online
      if (this.state.count > 0) {
        this.triggerResend();
      }
    }

    // Track offline state
    if (!this.props.online) {
      this.wasOffline = true;
    }
  }

  componentWillUnmount(): void {
    if (this.retryTimeout) {
      clearTimeout(this.retryTimeout);
    }
    if (this.successDismissTimeout) {
      clearTimeout(this.successDismissTimeout);
    }
  }

  triggerResend = async () => {
    if (this.state.syncing || !this.props.online) return;

    this.setState({ syncing: true, lastSyncSuccess: null });

    const result = await resend();

    this.setState({
      syncing: false,
      lastSyncSuccess: result.success,
      count: result.remaining,
    });

    // If there are still pending calls and we're online, retry after a delay
    if (result.remaining > 0 && this.props.online) {
      this.retryTimeout = setTimeout(() => {
        if (this.props.online && this.state.count > 0) {
          this.triggerResend();
        }
      }, 5000); // Retry after 5 seconds
    }

    // Auto-dismiss success message after 5 seconds
    if (result.success && result.remaining === 0) {
      this.successDismissTimeout = setTimeout(() => {
        this.setState({ lastSyncSuccess: null });
        this.wasOffline = false;
      }, 5000);
    }
  };

  handleRetryClick = () => {
    this.triggerResend();
  };

  render() {
    const { classes, online } = this.props;
    const { count, syncing, lastSyncSuccess } = this.state;

    // Show nothing if no pending calls and not syncing
    if (count === 0 && !syncing) {
      // Show brief success message after sync completes
      if (lastSyncSuccess === true && this.wasOffline) {
        return (
          <div className={`${classes.banner} ${classes.success}`}>
            <CloudDone className={classes.icon} />
            <span>All scores synced successfully</span>
          </div>
        );
      }
      return null;
    }

    // Syncing state
    if (syncing) {
      return (
        <div className={`${classes.banner} ${classes.syncing}`}>
          <CircularProgress size={16} className={classes.icon} color="inherit" />
          <span>
            Syncing {count} score{count !== 1 ? "s" : ""}...
          </span>
        </div>
      );
    }

    // Offline with pending calls
    if (!online) {
      return (
        <div className={`${classes.banner} ${classes.offline}`}>
          <CloudOff className={classes.icon} />
          <span>
            {count} score{count !== 1 ? "s" : ""} queued - waiting for connection
          </span>
        </div>
      );
    }

    // Online with pending calls (failed to sync)
    return (
      <Button
        fullWidth
        color="secondary"
        variant="contained"
        onClick={this.handleRetryClick}
        className={classes.retryButton}
        startIcon={<SyncIcon />}
      >
        {count} score{count !== 1 ? "s" : ""} pending - tap to retry
      </Button>
    );
  }
}

const styles = (theme: Theme) =>
  createStyles({
    banner: {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      padding: "8px 16px",
      fontSize: 14,
      fontWeight: 500,
    },
    icon: {
      marginRight: 8,
      fontSize: 18,
    },
    offline: {
      backgroundColor: "#ff9800",
      color: "#fff",
    },
    syncing: {
      backgroundColor: "#2196f3",
      color: "#fff",
    },
    success: {
      backgroundColor: "#4caf50",
      color: "#fff",
    },
    retryButton: {
      borderRadius: 0,
    },
  });

export default withDetector(withStyles(styles)(OfflineRequests));
